import { Dispatch } from '@reduxjs/toolkit';

import { showNotification } from '@Components/design-system/notification/notification';
import { NotificationType } from '@Components/design-system/notification/notification';
import { ActionConsts } from '@Definitions/ActionConsts';
import { IConsultationInvoice } from '@Reducers/billing';
import { OrderService } from '@Services';
import { BillingService } from '@Services/Billing';
import {
  ICreateConsultationInvoicePayload,
  ICreatePrescriptionInvoicePayload,
  IDrugFxPreference,
  IInventoryItemPurchase,
  ILabFxPreference,
  InvoiceMode,
  IRefundConsultationPayload,
} from '@Services/Billing/billing-payload';
import { CONSULTATION_TYPE, DEFAULT_LOCATION_CODE, INSTANT_LOCATION_CODE, ItemType } from '@Utils/constants';
import { formatInvoice, formatPrice, getPreviewFromOverview } from '@Utils/Helpers/invoice.helper';
import { InvoiceStatus } from '@Utils/Helpers/Order/helper';
import { LOG_ERROR } from '@Utils/logger';

export const BillingActions = {
  CreateConsultationInvoice:
    (requestPayload: ICreateConsultationInvoicePayload, noHold = false) =>
    async (dispatch: Dispatch) => {
      try {
        dispatch({ type: ActionConsts.Billing.GetConsultationInvoiceInit });
        const response = await (noHold
          ? BillingService.GetConsultationInvoiceWithoutHold({
              ...requestPayload,
              ...(requestPayload.mode === InvoiceMode.Postpaid && {
                attributes: {
                  createInvoice: true,
                  surchargeRate: requestPayload?.attributes?.surchargeRate,
                },
              }),
            })
          : BillingService.GetConsultationInvoice({
              ...requestPayload,
              ...(requestPayload.mode === InvoiceMode.Postpaid && {
                attributes: {
                  createInvoice: true,
                  surchargeRate: requestPayload?.attributes?.surchargeRate,
                },
              }),
            }));
        if (response?.status === 201) {
          dispatch({
            type: ActionConsts.Billing.GetConsultationInvoiceSuccess,
            invoice: formatInvoice(response?.data),
          });
        } else throw new Error(response?.data?.message);
      } catch (error) {
        const referenceData = {
          request: requestPayload ?? {},
          error: error ?? '',
        };
        LOG_ERROR('Error: BillingAction>>CreateConsultationInvoice', referenceData);
        dispatch({
          type: ActionConsts.Billing.GetConsultationInvoiceFail,
        });
        throw error;
      }
    },
  PreviewConsultationInvoice: (requestPayload: ICreateConsultationInvoicePayload) => async (dispatch: Dispatch) => {
    const locationCodes =
      requestPayload?.locationCode === DEFAULT_LOCATION_CODE &&
      requestPayload?.consultationCode === CONSULTATION_TYPE.SC
        ? [requestPayload?.locationCode, INSTANT_LOCATION_CODE]
        : [requestPayload?.locationCode];
    try {
      dispatch({
        type: ActionConsts.Billing.PreviewConsultationInvoiceInit,
        invoicePreviews: locationCodes.map((locationCode) => ({ isLoading: true, locationCode })),
      });
      const responses = await Promise.all(
        locationCodes.map((locationCode) =>
          BillingService.PreviewConsultationInvoice({
            ...requestPayload,
            locationCode,
            attributes: {
              ...requestPayload?.attributes,
              isInstant: locationCode === INSTANT_LOCATION_CODE,
              surchargeRate: requestPayload?.attributes?.surchargeRate,
            },
          }),
        ),
      );
      if (responses.every((response) => response?.status === 201)) {
        dispatch({
          type: ActionConsts.Billing.PreviewConsultationInvoiceSuccess,
          invoicePreviews: responses.map((response, index) =>
            formatInvoice({ ...response?.data, locationCode: locationCodes[index] }),
          ),
        });
      } else {
        dispatch({
          type: ActionConsts.Billing.PreviewConsultationInvoiceFail,
          invoicePreviews: responses.map((response, index) => ({
            isLoading: false,
            locationCode: locationCodes[index],
            error: response?.data?.message,
          })),
        });
      }
    } catch (error) {
      const referenceData = {
        error: error ?? '',
        requestPayload: requestPayload ?? {},
      };
      dispatch({
        type: ActionConsts.Billing.PreviewConsultationInvoiceFail,
        invoicePreviews: locationCodes.map((locationCode) => ({
          isLoading: false,
          locationCode,
          error: error?.message,
        })),
      });
      LOG_ERROR('Error: BillingAction>>PreviewConsultationInvoice', referenceData);
    }
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetConsultationInvoice: () => (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Billing.ResetConsultationInvoice,
    });
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetConsultationInvoicePreview: () => (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Billing.ResetConsultationInvoicePreview,
    });
  },
  GetInvoiceOverview: (requestPayload: ICreatePrescriptionInvoicePayload) => async (dispatch: Dispatch, getState) => {
    try {
      dispatch({
        type: ActionConsts.Billing.PreviewPrescriptionInvoicesInit,
        encounterId: requestPayload.encounterId,
      });
      const response = await BillingService.GetInvoiceOverview({
        ...requestPayload,
        ignoreWhenNoItems: true,
      });
      if (response?.status === 201) {
        const invoice = getPreviewFromOverview(
          response?.data,
          requestPayload.encounterId || response?.data?.encounterId,
        );
        if (invoice.encounterId) {
          dispatch({
            type: ActionConsts.Billing.PreviewPrescriptionInvoicesSuccess,
            invoice: invoice,
          });
        } else {
          dispatch({
            type: ActionConsts.Billing.PreviewPrescriptionInvoicesFail,
          });
        }
      } else {
        dispatch({
          type: ActionConsts.Billing.PreviewPrescriptionInvoicesFail,
        });
      }
    } catch (error) {
      const referenceData = {
        error: error ?? '',
        requestPayload: requestPayload ?? {},
      };
      dispatch({
        type: ActionConsts.Billing.PreviewPrescriptionInvoicesFail,
      });
      LOG_ERROR('Error: BillingAction>>PreviewPrescriptionInvoice', referenceData);
    }
  },
  HandleFxPreference:
    (payload: { encounterId: string; labFxPreference?: ILabFxPreference; drugFxPreference?: IDrugFxPreference }) =>
    async (dispatch: Dispatch) => {
      try {
        const { encounterId, labFxPreference, drugFxPreference } = payload;
        dispatch({
          type: ActionConsts.Billing.HandleFxPreference,
          fxPreference: {
            labFxPreference,
            drugFxPreference,
          },
          encounterId,
        });
      } catch (error) {
        showNotification(NotificationType.ERROR, error?.message, 5000);
        LOG_ERROR('Error: BillingAction>>HandleFxPreference', error);
      }
    },
  HandleItemSelection:
    (payload: { encounterId: string; typeId: string; quantity?: number; mandate?: boolean }) =>
    async (dispatch: Dispatch, getState) => {
      const { encounterId, typeId, quantity, mandate } = payload;
      const invoiceToModify = getState().billing.prescriptionInvoicePreviews.find(
        (invoice) => invoice.encounterId === encounterId,
      );
      if (!invoiceToModify) return;

      const updatedItems = invoiceToModify.items.map((item) => {
        if (item.typeId === typeId) {
          if (quantity) {
            return {
              ...item,
              selection: { quantity, isSelected: true },
            };
          }
          if (mandate) {
            return {
              ...item,
              selection: { quantity: item.quantity, isSelected: true },
            };
          }
          const currentSelection = item?.selection ? false : { quantity: item.quantity, isSelected: true };
          return {
            ...item,
            selection: currentSelection,
          };
        }
        return item;
      });
      try {
        dispatch({
          type: ActionConsts.Billing.HandleInvoiceItemSelection,
          encounterId,
          modifiedItems: updatedItems,
        });
      } catch (error) {
        LOG_ERROR('Error: BillingAction>>HandleItemSelection', error);
      }
    },
  CreatePrescriptionInvoice: (requestPayload: ICreatePrescriptionInvoicePayload) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Billing.GetPrescriptionInvoiceInit });
      const response = await BillingService.GetPrescriptionInvoice(requestPayload);
      if (response?.status === 201) {
        dispatch({
          type: ActionConsts.Billing.GetPrescriptionInvoiceSuccess,
          invoice: response?.data as IConsultationInvoice,
        });
      } else throw new Error('Axios Error');
    } catch (error) {
      const referenceData = {
        request: requestPayload ?? {},
        error: error ?? '',
      };
      if (error?.message !== 'Axios Error') LOG_ERROR('Error: BillingAction>>CreatePrescriptionInvoice', referenceData);
      dispatch({
        type: ActionConsts.Billing.GetPrescriptionInvoiceFail,
      });
      throw error;
    }
  },
  CreateInvoiceItemPurchase: (requestPayload: IInventoryItemPurchase) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Billing.CreateInventoryItemPurchaseInit });
      const response = await BillingService.GetInventoryItemPurchase(requestPayload);
      if (response?.status === 201) {
        dispatch({
          type: ActionConsts.Billing.CreateInventoryItemPurchaseSuccess,
          purchaseItem: response?.data,
        });
      } else throw new Error('Axios Error');
    } catch (error) {
      const referenceData = {
        request: requestPayload ?? {},
        error: error ?? '',
      };
      if (error?.message !== 'Axios Error') LOG_ERROR('Error: BillingAction>>CreateInvoiceItemPurchase', referenceData);
      dispatch({
        type: ActionConsts.Billing.GetPrescriptionInvoiceFail,
      });
      throw error;
    }
  },
  PreviewInventoryItemPurchase: (requestPayload: IInventoryItemPurchase) => async (dispatch: Dispatch) => {
    try {
      dispatch({
        type: ActionConsts.Billing.PreviewPrescriptionInvoicesInit,
        encounterId: 'direct',
      });
      const response = await BillingService.GetInventoryItemPurchasePreview(requestPayload);
      if (response?.status === 201) {
        if (response?.data) {
          dispatch({
            type: ActionConsts.Billing.PreviewPrescriptionInvoicesSuccess,
            invoice: { ...formatInvoice(response?.data), encounterId: 'direct' },
          });
        } else {
          dispatch({
            type: ActionConsts.Billing.PreviewPrescriptionInvoicesFail,
          });
        }
      } else {
        dispatch({
          type: ActionConsts.Billing.PreviewPrescriptionInvoicesFail,
        });
      }
    } catch (error) {
      const referenceData = {
        error: error ?? '',
        requestPayload: requestPayload ?? {},
      };
      dispatch({
        type: ActionConsts.Billing.PreviewPrescriptionInvoicesFail,
      });
      LOG_ERROR('Error: BillingAction>>PreviewInventoryItemPurchase', referenceData);
    }
  },
  CreateInstantConsultationInvoice:
    (requestPayload: ICreateConsultationInvoicePayload) => async (dispatch: Dispatch) => {
      try {
        dispatch({ type: ActionConsts.Billing.GetConsultationInvoiceInit });
        const response = await BillingService.CreateInstantConsultationInvoice(requestPayload);
        if (response?.status === 201) {
          dispatch({
            type: ActionConsts.Billing.GetConsultationInvoiceSuccess,
            invoice: formatInvoice(response?.data) as IConsultationInvoice,
          });
        } else {
          dispatch({
            type: ActionConsts.Billing.GetConsultationInvoiceFail,
          });
        }
      } catch (error) {
        const referenceData = {
          request: requestPayload ?? {},
          error: error ?? '',
        };
        LOG_ERROR('Error: BillingAction>>CreateInstantConsultationInvoice', referenceData);
        dispatch({
          type: ActionConsts.Billing.GetConsultationInvoiceFail,
        });
      }
    },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetPrescriptionInvoicePreview: () => (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Billing.ResetPrescriptionInvoicePreview,
    });
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  GetAllInvoices: () => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Billing.GetAllInvoicesInit });
      const response = await OrderService.GetPatientInvoices({ statuses: [InvoiceStatus.PAID] });
      if (response?.status === 200) {
        dispatch({
          type: ActionConsts.Billing.GetAllInvoicesSuccess,
          invoices: response?.data,
        });
      } else {
        dispatch({ type: ActionConsts.Billing.GetAllInvoicesFail });
      }
    } catch (error) {
      LOG_ERROR('Error: BillingAction>>GetAllInvoices', error);
      dispatch({ type: ActionConsts.Billing.GetAllInvoicesFail });
    }
  },
  MakeDrugsOptional:
    (requestPayload: { encounterId: string; locationId: string }) => async (dispatch: Dispatch, getState) => {
      const { encounterId, locationId } = requestPayload;
      const invoice: IConsultationInvoice = getState().billing.prescriptionInvoicePreviews.find(
        (invoice) => invoice.encounterId === encounterId,
      );
      if (!invoice) return;
      const unServiceableItems = invoice.unserviceableItems
        ?.filter((item) => item.type === ItemType.DRUG)
        .map((item) => ({
          ...item,
          payableAmount: formatPrice(
            item.availablePrices?.find((price) => price.locationId === locationId)?.payableAmount * item.quantity,
          ),
          listingAmount: formatPrice(
            item.availablePrices?.find((price) => price.locationId === locationId)?.listingAmount * item.quantity,
          ),
          selection: {
            isSelected: true,
            quantity: item.quantity,
          },
        }));
      const updatedInvoice = {
        ...invoice,
        items: [...invoice.items, ...unServiceableItems],
        unserviceableItems: invoice.unserviceableItems?.filter((item) => item.type !== ItemType.DRUG),
        showVenueNote: true,
      };
      dispatch({
        type: ActionConsts.Encounter.MakeDrugsOptional,
        encounterId: requestPayload.encounterId,
      });
      dispatch({
        type: ActionConsts.Billing.PreviewPrescriptionInvoicesSuccess,
        invoice: updatedInvoice,
      });
    },
  RefundConsultation:
    (requestPayload: IRefundConsultationPayload) =>
    async (dispatch: Dispatch): Promise<boolean> => {
      try {
        dispatch({ type: ActionConsts.Billing.RefundConsultationInit });
        const response = await BillingService.RefundConsultation(requestPayload);
        if (response?.status === 201) {
          dispatch({ type: ActionConsts.Billing.RefundConsultationSuccess });
          return true;
        } else {
          dispatch({ type: ActionConsts.Billing.RefundConsultationFail });
          return false;
        }
      } catch (error) {
        const referenceData = {
          request: requestPayload ?? {},
          error: error ?? '',
        };
        LOG_ERROR('Error: BillingAction>>RefundConsultation', referenceData);
        dispatch({ type: ActionConsts.Billing.RefundConsultationFail });
        throw error;
      }
    },
};
